import {useCallback, useEffect, useRef, useState} from 'react';
import {useDebounce} from 'react-use';
import {Alert, AlertTitle, Button} from '@ezcater/tapas';
import {Form, useFormikContext} from 'formik';
import Trans from 'next-translate/Trans';
import useTranslation from 'next-translate/useTranslation';

import {type TrackEvent} from '@/components/global/layout/EventBar/types';
import {useStoreNextRouter} from '@/components/global/providers/StoreNextRouter';
import Counter from '@/components/ui/Counter';
import TextField from '@/components/ui/TextField';
import {FulfillmentDetailFormProps} from './EventBar';

type DetailsProps = {
  trackEvent: TrackEvent;
};

const Details: React.FC<DetailsProps> = ({trackEvent}) => {
  const {t} = useTranslation('app-bar');
  const {isOnMenuPage, isOnSearchPage} = useStoreNextRouter();
  const {status, values, setFieldValue, isSubmitting} =
    useFormikContext<FulfillmentDetailFormProps>();

  // Debounce so rapid counter +/- (or typing) collapses into a single settled headcount-updated
  const [pendingHeadcount, setPendingHeadcount] = useState<number | null>(null);
  // Latest pending value, read by the stable `flushHeadcount` below (kept off its deps so the
  // unmount cleanup fires only on unmount).
  const pendingHeadcountRef = useRef<number | null>(null);
  const trackedHeadcountRef = useRef<number | null>(null);
  // trackEvent is rebuilt on every EventBar render; read it through a ref so `flushHeadcount` stays
  // stable and the unmount cleanup below fires only on unmount.
  const trackEventRef = useRef(trackEvent);
  useEffect(() => {
    trackEventRef.current = trackEvent;
  });

  const flushHeadcount = useCallback(() => {
    const value = pendingHeadcountRef.current;
    if (value != null && value !== trackedHeadcountRef.current) {
      trackedHeadcountRef.current = value;
      trackEventRef.current('headcount-updated', {headcountNumber: value});
    }
  }, []);

  useDebounce(flushHeadcount, 500, [pendingHeadcount]);

  // Flush on unmount so the headcount-updated event isn't lost.
  useEffect(() => () => flushHeadcount(), [flushHeadcount]);

  const handleHeadcountChange = (newValue: number | null) => {
    setFieldValue('attendees', newValue);
    pendingHeadcountRef.current = newValue;
    setPendingHeadcount(newValue);
  };

  // The event name has no explicit commit, so track it on blur — and only when it actually changed
  // since the last tracked value, so tabbing through the field doesn't log a no-op update.
  const lastTrackedNameRef = useRef(values.eventName);

  const handleEventNameBlur = () => {
    if (values.eventName !== lastTrackedNameRef.current) {
      lastTrackedNameRef.current = values.eventName;
      trackEvent('event-name-updated', {eventName: values.eventName});
    }
  };

  const getCTALabel = () => {
    if (isOnSearchPage) {
      return t('eventBar.eventDetails.cta.search');
    } else if (isOnMenuPage) {
      return t('eventBar.eventDetails.cta.menu');
    } else {
      return t('eventBar.eventDetails.cta.accountPage');
    }
  };

  return (
    <Form className="mb-4 flex flex-col gap-4">
      {status?.error && (
        <Alert className="w-[350px]" variant="critical">
          <AlertTitle>{status.error.message}</AlertTitle>
        </Alert>
      )}
      {/* --- Attendees --- */}
      <div className="flex items-center justify-between">
        <label className="font-bold" htmlFor="event-details-attendees">
          {t('eventBar.eventDetails.attendees.label')}
        </label>

        <div className="flex gap-2">
          <Counter
            id="event-details-attendees"
            inputValue={values.attendees}
            onChange={newValue =>
              handleHeadcountChange(typeof newValue === 'number' ? newValue : null)
            }
            decimalScale={0}
            fixedDecimalScale
          />
        </div>
      </div>

      {/* --- Event name --- */}
      <div className="flex flex-col gap-2">
        <label htmlFor="event-details-name">
          <Trans
            i18nKey="app-bar:eventBar.eventDetails.eventName.label"
            components={{strong: <strong />}}
          />
        </label>

        <div className="flex gap-2">
          <TextField.Root>
            <TextField.Input
              id="event-details-name"
              onBlur={handleEventNameBlur}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setFieldValue('eventName', event.target.value)
              }
              type="text"
              value={values.eventName ?? ''}
            />
          </TextField.Root>
        </div>
      </div>

      <Button type="submit" loading={isSubmitting}>
        {getCTALabel()}
      </Button>
    </Form>
  );
};

export default Details;
